import React, { useState } from 'react';
import { 
  EyeSlashIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface MaskingRule {
  id: string;
  type: string;
  pattern: string;
  maskWith: string;
  enabled: boolean;
  description: string;
}

export const ConfigureDataMaskingPage: React.FC = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<MaskingRule[]>([
    {
      id: '1',
      type: 'phone',
      pattern: '+XX-XXX-XXX-XX89',
      maskWith: 'X',
      enabled: true,
      description: 'Standard phone number masking'
    },
    {
      id: '2',
      type: 'email',
      pattern: 'j***@example.com',
      maskWith: '*',
      enabled: true,
      description: 'Standard email address masking'
    }
  ]);

  const [newRule, setNewRule] = useState<Omit<MaskingRule, 'id'>>({
    type: '',
    pattern: '',
    maskWith: '',
    enabled: true,
    description: ''
  });

  const [customType, setCustomType] = useState('');

  const handleAddRule = () => {
    if (newRule.type && newRule.pattern && newRule.maskWith) {
      setRules([...rules, { 
        ...newRule, 
        id: Date.now().toString(),
        type: newRule.type === 'custom' ? customType : newRule.type 
      }]);
      setNewRule({
        type: '',
        pattern: '',
        maskWith: '',
        enabled: true,
        description: ''
      });
      setCustomType('');
    }
  };

  const handleDeleteRule = (id: string) => {
    setRules(rules.filter(rule => rule.id !== id));
  };

  const handleToggleRule = (id: string) => {
    setRules(rules.map(rule => 
      rule.id === id ? { ...rule, enabled: !rule.enabled } : rule
    ));
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-6">
          <EyeSlashIcon className="w-8 h-8 text-[#FF5722] mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Configure Data Masking Rules</h1>
        </div>

        {/* Add New Rule Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Add New Masking Rule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Rule Type</label>
                <select
                  value={newRule.type}
                  onChange={(e) => {
                    setNewRule({ ...newRule, type: e.target.value });
                    if (e.target.value !== 'custom') {
                      setCustomType('');
                    }
                  }}
                  className="mt-1 block w-full border rounded-md p-2"
                >
                  <option value="">Select Type</option>
                  <option value="phone">Phone Number</option>
                  <option value="email">Email Address</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              {/* Custom Type Input - Only shows when 'custom' is selected */}
              {newRule.type === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Custom Rule Type Name</label>
                  <input
                    type="text"
                    value={customType}
                    onChange={(e) => setCustomType(e.target.value)}
                    placeholder="Enter custom rule type name"
                    className="mt-1 block w-full border rounded-md p-2"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Pattern</label>
                <input
                  type="text"
                  value={newRule.pattern}
                  onChange={(e) => setNewRule({ ...newRule, pattern: e.target.value })}
                  placeholder="e.g., +XX-XXX-XXX-XXXX"
                  className="mt-1 block w-full border rounded-md p-2"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Mask Character</label>
                <input
                  type="text"
                  value={newRule.maskWith}
                  onChange={(e) => setNewRule({ ...newRule, maskWith: e.target.value })}
                  placeholder="e.g., *"
                  className="mt-1 block w-full border rounded-md p-2"
                  maxLength={1}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <input
                  type="text"
                  value={newRule.description}
                  onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                  placeholder="Rule description"
                  className="mt-1 block w-full border rounded-md p-2"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleAddRule}
              className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Rule
            </button>
          </div>
        </div>

        {/* Existing Rules Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Existing Rules</h2>
          <div className="space-y-4">
            {rules.map((rule) => (
              <div key={rule.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900 capitalize">{rule.type} Masking</h3>
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                        rule.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Pattern: {rule.pattern}</p>
                    <p className="text-sm text-gray-600">Mask: {rule.maskWith}</p>
                    <p className="text-sm text-gray-600">{rule.description}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handleToggleRule(rule.id)}
                      className={`p-2 rounded-md ${
                        rule.enabled ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <CheckCircleIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="p-2 text-red-600 hover:text-red-800 rounded-md"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={() => navigate('/superadmin/security')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => navigate('/superadmin/security')}
            className="bg-[#FF5722] text-white px-6 py-2 rounded-md hover:bg-[#F4511E]"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigureDataMaskingPage; 